import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEur, fmtInt, MONTHS_ES, fmtDate } from "@/lib/api";
import { PageHeader, Section, Pill, EmptyState } from "@/components/ui-kit";
import { Modal } from "./Books";
import { Plus, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Settlements() {
  const [items, setItems] = useState([]);
  const [dists, setDists] = useState([]);
  const [books, setBooks] = useState([]);
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState(null);

  async function load() {
    const [s, d, b] = await Promise.all([api.get("/settlements"), api.get("/distributors"), api.get("/books")]);
    setItems(s.data); setDists(d.data); setBooks(b.data);
  }
  useEffect(() => { load(); }, []);

  const distById = Object.fromEntries(dists.map(d => [d.id, d]));

  async function confirm(id) {
    if (!window.confirm("Confirmar la liquidación generará movimientos de stock irreversibles. ¿Continuar?")) return;
    try {
      const { data } = await api.post(`/settlements/${id}/confirm`);
      toast.success(`Confirmada · ${data.movements_created} movimientos`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }

  async function del(id) {
    if (!window.confirm("¿Eliminar liquidación (sólo borradores)?")) return;
    try {
      await api.delete(`/settlements/${id}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }

  return (
    <div data-testid="settlements-page">
      <PageHeader
        title="Liquidaciones mensuales"
        subtitle="Confirmar una liquidación da salida del depósito a las unidades vendidas y reintegra las devueltas al almacén propio."
        actions={
          <button onClick={() => setShow(true)} data-testid="new-settlement-button"
            className="bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2">
            <Plus size={16} strokeWidth={1.5} /> Nueva liquidación
          </button>
        }
      />

      <Section>
        {items.length === 0 ? (
          <EmptyState title="Sin liquidaciones" description="Crea una liquidación mensual para un distribuidor con sus cantidades vendidas y devueltas." testid="settlements-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-[0.1em] text-ink2 bg-sidebar/40">
                <th className="text-left px-5 py-3 font-medium">Periodo</th>
                <th className="text-left px-5 py-3 font-medium">Distribuidor</th>
                <th className="text-right px-5 py-3 font-medium">Vendidos</th>
                <th className="text-right px-5 py-3 font-medium">Devueltos</th>
                <th className="text-right px-5 py-3 font-medium">Importe PVP</th>
                <th className="text-right px-5 py-3 font-medium">Neto editorial</th>
                <th className="text-left px-5 py-3 font-medium">Estado</th>
                <th className="text-right px-5 py-3 font-medium">Acciones</th>
              </tr></thead>
              <tbody>
                {items.map(s => (
                  <tr key={s.id} className="border-t border-line hover:bg-sidebar/30" data-testid={`settlement-row-${s.id}`}>
                    <td className="px-5 py-3 font-medium">{MONTHS_ES[s.period_month - 1]} {s.period_year}</td>
                    <td className="px-5 py-3">{distById[s.distributor_id]?.name || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtInt(s.total_sold)}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtInt(s.total_returned)}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtEur(s.total_amount_pvp)}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtEur(s.net_amount)}</td>
                    <td className="px-5 py-3">
                      <Pill tone={s.status === "confirmada" ? "success" : "warning"}>{s.status}</Pill>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setDetail(s)} className="text-xs text-ink2 hover:text-ink mr-3">Ver</button>
                      {s.status !== "confirmada" && (
                        <>
                          <button onClick={() => confirm(s.id)} data-testid={`confirm-settlement-${s.id}`} className="text-xs text-olive hover:opacity-80 mr-3 inline-flex items-center gap-1">
                            <CheckCircle2 size={13} /> Confirmar
                          </button>
                          <button onClick={() => del(s.id)} className="text-xs text-crimson hover:opacity-80 inline-flex items-center gap-1">
                            <Trash2 size={13} /> Borrar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {show && <SettlementForm dists={dists} books={books} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {detail && <SettlementDetail settlement={detail} books={books} dists={dists} onClose={() => setDetail(null)} />}
    </div>
  );
}

function SettlementForm({ dists, books, onClose, onSaved }) {
  const now = new Date();
  const [distributor_id, setDist] = useState(dists[0]?.id || "");
  const [period_year, setY] = useState(now.getFullYear());
  const [period_month, setM] = useState(now.getMonth() + 1);
  const [lines, setLines] = useState(books.map(b => ({
    book_id: b.id, isbn: b.isbn, title: b.title, pvp: b.pvp,
    sold_qty: 0, returned_qty: 0, damaged_qty: 0, promotional_qty: 0, unit_price: b.pvp,
  })));
  const [notes, setNotes] = useState("");

  function update(i, k, v) {
    setLines(L => L.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  }

  const totals = useMemo(() => {
    const sold = lines.reduce((a, l) => a + (parseInt(l.sold_qty) || 0), 0);
    const ret = lines.reduce((a, l) => a + (parseInt(l.returned_qty) || 0), 0);
    const amt = lines.reduce((a, l) => a + (parseInt(l.sold_qty) || 0) * (parseFloat(l.unit_price) || 0), 0);
    return { sold, ret, amt };
  }, [lines]);

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = {
        distributor_id, period_year: parseInt(period_year), period_month: parseInt(period_month),
        notes,
        lines: lines
          .filter(l => (parseInt(l.sold_qty) || 0) + (parseInt(l.returned_qty) || 0) + (parseInt(l.damaged_qty) || 0) > 0)
          .map(l => ({
            book_id: l.book_id,
            sold_qty: parseInt(l.sold_qty) || 0,
            returned_qty: parseInt(l.returned_qty) || 0,
            damaged_qty: parseInt(l.damaged_qty) || 0,
            promotional_qty: parseInt(l.promotional_qty) || 0,
            unit_price: parseFloat(l.unit_price) || 0,
          })),
      };
      if (!payload.distributor_id) { toast.error("Selecciona un distribuidor"); return; }
      if (payload.lines.length === 0) { toast.error("Añade al menos una línea con cantidades"); return; }
      await api.post("/settlements", payload);
      toast.success("Liquidación creada en borrador");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }

  return (
    <Modal title="Nueva liquidación" onClose={onClose} size="xl">
      <form onSubmit={submit} className="space-y-5" data-testid="settlement-form">
        <div className="grid grid-cols-3 gap-4">
          <F label="Distribuidor" required>
            <select required value={distributor_id} onChange={(e) => setDist(e.target.value)} data-testid="settlement-distributor" className={IN}>
              <option value="">—</option>
              {dists.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </F>
          <F label="Año"><input type="number" value={period_year} onChange={(e) => setY(e.target.value)} className={IN} /></F>
          <F label="Mes">
            <select value={period_month} onChange={(e) => setM(e.target.value)} className={IN}>
              {MONTHS_ES.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
            </select>
          </F>
        </div>

        <div className="border border-line rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="settlement-lines-table">
              <thead><tr className="text-[11px] uppercase tracking-[0.1em] text-ink2 bg-sidebar/40">
                <th className="text-left px-3 py-2">Título</th>
                <th className="text-right px-3 py-2 w-24">Vendidos</th>
                <th className="text-right px-3 py-2 w-24">Devueltos</th>
                <th className="text-right px-3 py-2 w-24">Deteriorados</th>
                <th className="text-right px-3 py-2 w-28">PVP unit.</th>
                <th className="text-right px-3 py-2 w-28">Total</th>
              </tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.book_id} className="border-t border-line">
                    <td className="px-3 py-2"><div className="truncate max-w-[260px]">{l.title}</div><div className="text-[11px] font-mono text-ink2">{l.isbn}</div></td>
                    <td className="px-3 py-1"><input type="number" min="0" value={l.sold_qty} onChange={(e) => update(i, "sold_qty", e.target.value)} className={IN + " text-right font-mono"} data-testid={`settlement-line-sold-${l.isbn}`} /></td>
                    <td className="px-3 py-1"><input type="number" min="0" value={l.returned_qty} onChange={(e) => update(i, "returned_qty", e.target.value)} className={IN + " text-right font-mono"} /></td>
                    <td className="px-3 py-1"><input type="number" min="0" value={l.damaged_qty} onChange={(e) => update(i, "damaged_qty", e.target.value)} className={IN + " text-right font-mono"} /></td>
                    <td className="px-3 py-1"><input type="number" step="0.01" value={l.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} className={IN + " text-right font-mono"} /></td>
                    <td className="px-3 py-2 text-right font-mono text-ink">{fmtEur((parseInt(l.sold_qty) || 0) * (parseFloat(l.unit_price) || 0))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-sidebar/50 border-t-2 border-line">
                  <td className="px-3 py-2 font-medium">Totales</td>
                  <td className="px-3 py-2 text-right font-mono font-medium">{fmtInt(totals.sold)}</td>
                  <td className="px-3 py-2 text-right font-mono font-medium">{fmtInt(totals.ret)}</td>
                  <td colSpan={2}></td>
                  <td className="px-3 py-2 text-right font-mono font-medium">{fmtEur(totals.amt)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <F label="Notas"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={IN} /></F>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-line rounded-sm hover:bg-sidebar">Cancelar</button>
          <button type="submit" data-testid="settlement-submit" className="px-4 py-2 text-sm bg-brand hover:bg-brand-hover text-white rounded-sm">Guardar borrador</button>
        </div>
      </form>
    </Modal>
  );
}

function SettlementDetail({ settlement: s, books, dists, onClose }) {
  const bById = Object.fromEntries(books.map(b => [b.id, b]));
  const dist = dists.find(d => d.id === s.distributor_id);
  return (
    <Modal title={`Liquidación · ${MONTHS_ES[s.period_month - 1]} ${s.period_year}`} onClose={onClose} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <Info k="Distribuidor" v={dist?.name || "—"} />
          <Info k="Comisión" v={`${s.commission_pct}%`} />
          <Info k="Estado" v={<Pill tone={s.status === "confirmada" ? "success" : "warning"}>{s.status}</Pill>} />
          <Info k="Creada" v={fmtDate(s.created_at)} />
          {s.confirmed_at && <Info k="Confirmada" v={fmtDate(s.confirmed_at)} />}
          <Info k="Neto editorial" v={fmtEur(s.net_amount)} />
        </div>
        <table className="w-full text-sm border border-line rounded-sm">
          <thead><tr className="bg-sidebar/40 text-[11px] uppercase tracking-[0.1em] text-ink2">
            <th className="text-left px-3 py-2">Libro</th>
            <th className="text-right px-3 py-2">Vendidos</th>
            <th className="text-right px-3 py-2">Devueltos</th>
            <th className="text-right px-3 py-2">Deteriorados</th>
            <th className="text-right px-3 py-2">PVP</th>
            <th className="text-right px-3 py-2">Total</th>
          </tr></thead>
          <tbody>
            {s.lines.map((l, i) => (
              <tr key={i} className="border-t border-line">
                <td className="px-3 py-2">{bById[l.book_id]?.title || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtInt(l.sold_qty)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtInt(l.returned_qty)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtInt(l.damaged_qty)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtEur(l.unit_price)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtEur(l.sold_qty * l.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {s.notes && <div className="text-sm text-ink2 italic">{s.notes}</div>}
      </div>
    </Modal>
  );
}

function Info({ k, v }) {
  return <div><div className="text-[11px] uppercase tracking-[0.1em] text-ink2">{k}</div><div className="mt-0.5">{v}</div></div>;
}
const IN = "w-full bg-white border border-line rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";
function F({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.12em] text-ink2">{label}{required && <span className="text-brand"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
